import type { IChainClient, Address, Micro, TxRecord } from '@shared/chain';
import type { IdentityService } from './IdentityService';
import type { IStorage } from '@/storage/IStorage';
import { dpbToMicro } from '@shared/chain';
import { bytesToBase64 } from '@/lib/format';
import { canonicalBytes } from '@/crypto/canonical';
import { MeshLinkError } from '@/lib/result';
import { createLogger } from '@/lib/logger';
import { useWalletStore } from '@/store/wallet';

const logger = createLogger('service:wallet');

export interface WalletServiceDeps {
  chain: IChainClient;
  identity: IdentityService;
  storage: IStorage;
}

export class WalletService {
  constructor(private readonly deps: WalletServiceDeps) {}

  async getBalance(): Promise<Micro> {
    const addr = this.deps.identity.requireUnlocked().address as Address;
    const bal = await this.deps.chain.getBalance(addr);
    useWalletStore.getState().setBalance(bal);
    return bal;
  }

  async claimFaucet(): Promise<void> {
    const res = await this.deps.identity.claimFaucet();
    useWalletStore.getState().setBalance(res.balanceMicro);
    useWalletStore.getState().setFaucetClaimed(true);
  }

  async registerDid(): Promise<void> {
    await this.deps.identity.registerDid();
  }

  async transfer(to: string, amountDpb: string): Promise<TxRecord> {
    const keys = this.deps.identity.requireUnlocked();
    const from = keys.address as Address;
    const toTrim = to.trim();
    const toAddr = (toTrim.startsWith('0x') ? toTrim : `0x${toTrim}`) as Address;
    if (!/^0x[0-9a-fA-F]{40}$/.test(toAddr)) throw new MeshLinkError('E_INVALID_INPUT', '收款地址格式不正确');
    let amountMicro: string;
    try {
      amountMicro = dpbToMicro(amountDpb).toString();
    } catch {
      throw new MeshLinkError('E_INVALID_INPUT', '金额格式不正确');
    }
    if (amountMicro === '0') throw new MeshLinkError('E_INVALID_INPUT', '转账金额必须大于 0');
    const signature = bytesToBase64(this.deps.identity.sign(canonicalBytes({ from, to: toAddr, amountMicro })));
    const tx = await this.deps.chain.transfer({ from, to: toAddr, amountMicro, signature });
    await this.deps.storage.txs.put(tx);
    useWalletStore.getState().addTx(tx);
    const bal = await this.deps.chain.getBalance(from);
    useWalletStore.getState().setBalance(bal);
    logger.info('转账完成', { to: toAddr, amountMicro });
    return tx;
  }

  async history(limit = 50): Promise<TxRecord[]> {
    const addr = this.deps.identity.requireUnlocked().address as Address;
    const txs = await this.deps.chain.getTransactionHistory(addr, limit);
    for (const t of txs) await this.deps.storage.txs.put(t).catch(() => undefined);
    useWalletStore.getState().setTxs(txs);
    return txs;
  }
}

export default WalletService;
